
import React, { createContext, useContext, useState, useCallback } from "react";
import { ProductDescription, Collection } from "@/lib/api";
import { productService } from "@/services/productService";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { operationQueue } from "@/utils/operationQueue";

interface CollectionsContextState {
  // Action status tracking
  improvingIds: string[];
  editingId: string | null;
  editedDescription: string;
  isProcessingAction: boolean;
  
  // Collection operations
  handleImprove: (description: ProductDescription) => Promise<void>;
  handleApprove: (description: ProductDescription) => Promise<void>;
  handleRevoke: (description: ProductDescription) => Promise<void>;
  handleEdit: (description: ProductDescription) => void;
  handleSaveEdit: (description: ProductDescription, editedText: string, originalText?: string) => Promise<void>;
  handleCancelEdit: () => void;
  handleDelete: (description: ProductDescription) => Promise<void>;
  fixGroupConsistency: (collectionId: string) => Promise<{ fixed: number, groups: number }>;
  refreshData: (collectionId?: string) => void;
  
  // State updates
  setEditedDescription: (description: string) => void;
}

const CollectionsContext = createContext<CollectionsContextState | undefined>(undefined);

export const CollectionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [improvingIds, setImprovingIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editedDescription, setEditedDescription] = useState<string>("");
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const queryClient = useQueryClient();

  // Optimized query invalidation with reduced scope
  const invalidateQueries = useCallback((collectionId?: string) => {
    // Use more targeted invalidation for better performance
    const invalidationPromises = [];
    
    if (collectionId) {
      // Only invalidate specific collection data
      invalidationPromises.push(
        queryClient.invalidateQueries({ 
          queryKey: ["productDescriptions", collectionId],
          exact: false 
        }),
        queryClient.invalidateQueries({ 
          queryKey: ["descriptions", collectionId],
          exact: false 
        }),
        queryClient.invalidateQueries({ 
          queryKey: ["collectionStats", collectionId],
          exact: false 
        })
      );
    } else {
      // Broader invalidation only when necessary
      invalidationPromises.push(
        queryClient.invalidateQueries({ 
          queryKey: ["collections"],
          exact: false 
        })
      );
    }
    
    // Execute invalidations without blocking
    Promise.all(invalidationPromises).catch(console.warn);
  }, [queryClient]);

  // Generic action handler with status tracking
  const handleAction = useCallback(async <T extends any>(
    actionName: string,
    description: ProductDescription,
    action: () => Promise<T>,
    successMessage: string,
    errorMessage: string,
    affectsIds: string[] = [description.id]
  ) => {
    setIsProcessingAction(true);
    
    try {
      // Update UI optimistically for better responsiveness
      if (actionName === 'improve') {
        setImprovingIds(prev => [...prev, ...affectsIds]);
      }
      
      // Execute the action
      const result = await action();
      
      // Show success message
      toast.success(successMessage);
      
      // Refresh data
      invalidateQueries(description.collection_id);
      
      return result;
    } catch (error) {
      console.error(`Error ${actionName}ing description:`, error);
      toast.error(errorMessage, {
        description: error instanceof Error ? error.message : "Please try again"
      });
    } finally {
      // Clean up UI state
      if (actionName === 'improve') {
        setImprovingIds(prev => prev.filter(id => !affectsIds.includes(id)));
      }
      
      setIsProcessingAction(false);
    }
  }, [invalidateQueries]);

  // Improve description
  const handleImprove = useCallback(async (description: ProductDescription) => {
    const affectedIds = description.content_id 
      ? [description.id, `group-${description.content_id}`] 
      : [description.id];
      
    await handleAction(
      'improve',
      description,
      () => productService.improveDescription(description),
      "Description improved successfully",
      "Failed to improve description",
      affectedIds
    );
  }, [handleAction]);

  // Approve description with optimistic updates
  const handleApprove = useCallback(async (description: ProductDescription) => {
    // Optimistic update - immediately update the cache
    queryClient.setQueryData(
      ["productDescriptions", description.collection_id],
      (oldData: any) => {
        if (!oldData) return oldData;
        
        return oldData.map((item: ProductDescription) => {
          // Update all items with same content_id or this specific item
          if (description.content_id && item.content_id === description.content_id) {
            return { ...item, status: "approved" };
          }
          if (!description.content_id && item.id === description.id) {
            return { ...item, status: "approved" };
          }
          return item;
        });
      }
    );

    // Also update approval status cache optimistically
    queryClient.setQueryData(
      ["collectionApprovalStatus", description.collection_id],
      (oldData: any) => {
        if (!oldData) return oldData;
        // Optimistically assume this approval might complete the collection
        return { ...oldData, pendingGroupsCount: Math.max(0, oldData.pendingGroupsCount - 1) };
      }
    );

    try {
      await productService.approveDescription(description);
      toast.success("Description approved successfully");
      invalidateQueries(description.collection_id);
    } catch (error) {
      // Revert optimistic update on error
      invalidateQueries(description.collection_id);
      console.error('Error approving description:', error);
      toast.error("Failed to approve description", {
        description: error instanceof Error ? error.message : "Please try again"
      });
    }
  }, [queryClient, invalidateQueries]);

  // Revoke approval with optimistic updates
  const handleRevoke = useCallback(async (description: ProductDescription) => {
    // Optimistic update - immediately update the cache
    queryClient.setQueryData(
      ["productDescriptions", description.collection_id],
      (oldData: any) => {
        if (!oldData) return oldData;
        
        return oldData.map((item: ProductDescription) => {
          // Update all items with same content_id or this specific item
          if (description.content_id && item.content_id === description.content_id) {
            return { ...item, status: "pending" };
          }
          if (!description.content_id && item.id === description.id) {
            return { ...item, status: "pending" };
          }
          return item;
        });
      }
    );

    // Also update approval status cache optimistically
    queryClient.setQueryData(
      ["collectionApprovalStatus", description.collection_id],
      (oldData: any) => {
        if (!oldData) return oldData;
        return { ...oldData, pendingGroupsCount: oldData.pendingGroupsCount + 1, isFullyApproved: false };
      }
    );

    try {
      await productService.revokeApproval(description);
      toast.success("Approval revoked successfully");
      invalidateQueries(description.collection_id);
    } catch (error) {
      // Revert optimistic update on error
      invalidateQueries(description.collection_id);
      console.error('Error revoking approval:', error);
      toast.error("Failed to revoke approval", {
        description: error instanceof Error ? error.message : "Please try again"
      });
    }
  }, [queryClient, invalidateQueries]);

  // Delete description
  const handleDelete = useCallback(async (description: ProductDescription) => {
    if (!window.confirm("Are you sure you want to delete this description?")) {
      return;
    }
    
    await handleAction(
      'delete',
      description,
      () => productService.deleteDescription(description),
      "Description deleted successfully",
      "Failed to delete description"
    );
  }, [handleAction]);

  // Edit description
  const handleEdit = useCallback((description: ProductDescription) => {
    setEditingId(description.id);
    setEditedDescription(description.improved_description || description.generated_description);
  }, []);

  // Save edited description
  const handleSaveEdit = useCallback(async (description: ProductDescription, editedText: string, originalText?: string) => {
    await handleAction(
      'save-edit',
      description,
      () => productService.editDescription(description, editedText, originalText),
      "Description updated successfully",
      "Failed to update description"
    );
    
    setEditingId(null);
  }, [handleAction]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditedDescription("");
  }, []);
  
  // Fix group consistency
  const fixGroupConsistency = useCallback(async (collectionId: string) => {
    setIsProcessingAction(true);
    
    try {
      const result = await productService.fixInconsistentGroups(collectionId);
      
      if (result.groups > 0) {
        toast.success(`Fixed ${result.groups} inconsistent product groups`);
      } else {
        toast.info("No inconsistent groups found");
      }
      
      invalidateQueries(collectionId);
      return result;
    } catch (error) {
      console.error("Error fixing group consistency:", error);
      toast.error("Failed to fix group consistency", {
        description: error instanceof Error ? error.message : "Please try again"
      });
      return { fixed: 0, groups: 0 };
    } finally {
      setIsProcessingAction(false);
    }
  }, [invalidateQueries]);
  
  // Refresh data
  const refreshData = useCallback((collectionId?: string) => {
    invalidateQueries(collectionId);
  }, [invalidateQueries]);

  const contextValue: CollectionsContextState = {
    improvingIds,
    editingId,
    editedDescription,
    isProcessingAction,
    handleImprove,
    handleApprove,
    handleRevoke,
    handleEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleDelete,
    fixGroupConsistency,
    refreshData,
    setEditedDescription,
  };

  return (
    <CollectionsContext.Provider value={contextValue}>
      {children}
    </CollectionsContext.Provider>
  );
};

export const useCollections = () => {
  const context = useContext(CollectionsContext);
  if (context === undefined) {
    throw new Error("useCollections must be used within a CollectionsProvider");
  }
  return context;
};
